/* import __COLOCATED_TEMPLATE__ from './suggestions.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { DeduplicatedAsyncData } from 'embercom/resources/utils/async-data';
import type KnowledgeHubEditorService from 'embercom/services/knowledge-hub-editor-service';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type ArticleContent from 'embercom/models/articles/article-content';
import type ContentSnippet from 'embercom/models/content-service/content-snippet';
import {
  ContentAction,
  type LocalizedKnowledgeContent,
} from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import type Model from '@ember-data/model';
import { EntityType } from 'embercom/models/data/entity-types';
import type Store from '@ember-data/store';
import { objectNames } from 'embercom/models/data/matching-system/matching-constants';
import type ContentSectionRelationship from 'embercom/models/content-service/content-section-relationship';
import type RouterService from '@ember/routing/router-service';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type SuggestionService from 'embercom/services/suggestion-service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { captureException } from 'embercom/lib/sentry';
import { timeout } from 'ember-concurrency';
import ENV from 'embercom/config/environment';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';
import type IntlService from 'ember-intl/services/intl';
import type OptimizeSuggestionsService from 'embercom/services/optimize-suggestions-service';
import type { OptimizeSuggestionItem } from 'embercom/services/optimize-suggestions-service';
import type { ConversationExploreConversation } from 'embercom/components/reporting/automation/ai-insights/conversation-panel';

interface Signature {
  Args: {
    activeContentId?: string;
    activeContentType?: string;
  };
}

const NEXUS_TOPIC = 'suggestions-generation-finished';
const NEXUS_EVENT_NAME = 'SuggestionsGenerationFinished';

export default class Suggestions extends Component<Signature> {
  @service declare appService: AppService;
  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare navbarCollapsingService: $TSFixMe;
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
  @service declare suggestionService: SuggestionService;
  @service declare realTimeEventService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare optimizeSuggestionsService: OptimizeSuggestionsService;
  @service declare intl: IntlService;

  @tracked private isHandlingTransition = false;
  @tracked private segmentationSettingsSideDrawerOpen = false;
  @tracked isFilterPanelOpen = false;

  @tracked showConversationPanel = false;
  @tracked selectedConversation: ConversationExploreConversation | undefined = undefined;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.router.on('routeWillChange', this.warnIfUnsavedChanges);
    this.realTimeEventService.subscribeTopics([NEXUS_TOPIC]);
    this.realTimeEventService.on(NEXUS_EVENT_NAME, this, 'handleSuggestionsGenerationFinished');
  }

  willDestroy() {
    super.willDestroy();
    this.router.off('routeWillChange', this.warnIfUnsavedChanges);
    this.realTimeEventService.unsubscribeTopics([NEXUS_TOPIC]);
    this.realTimeEventService.off(NEXUS_EVENT_NAME, this, 'handleSuggestionsGenerationFinished');
  }

  get suggestions() {
    return this.optimizeSuggestionsService.suggestions;
  }

  get app() {
    return this.appService.app;
  }

  get isSubmenuCollapsed() {
    return this.navbarCollapsingService.collapsed;
  }

  get isStandaloneApp() {
    return this.appService.app.canUseStandalone;
  }

  get hasSuggestions() {
    return this.suggestions.length > 0;
  }

  get suggestionList() {
    return Object.values(this.suggestions);
  }

  get suggestionCount() {
    return this.suggestionList.length;
  }

  get activeSuggestion(): OptimizeSuggestionItem {
    return this.optimizeSuggestionsService.findSuggestionByEntityId(
      this.args.activeContentId?.toString() || '',
    ) as OptimizeSuggestionItem;
  }

  get activeContent() {
    return this.knowledgeHubEditorService.activeContentModel;
  }

  get loadingActiveContent(): boolean {
    return this.activeContentLoader.isLoading;
  }

  get shouldShowSideBySideReview() {
    return this.args.activeContentType === objectNames[EntityType.ContentSectionRelationship];
  }

  get contentSectionRelationship() {
    return this.knowledgeHubEditorService.reviewableActiveContent as ContentSectionRelationship;
  }

  private activeContentLoader = DeduplicatedAsyncData(
    this,
    () => [this.args.activeContentId, this.args.activeContentType],
    async (activeContentId, activeContentType) => {
      if (!activeContentId || !activeContentType) {
        return null;
      }

      if (this.isHandlingTransition) {
        return this.activeContent;
      }

      if (activeContentType === objectNames[EntityType.ContentSectionRelationship]) {
        return await this.fetchContentSectionRelationshipContents(Number(activeContentId));
      }

      return await this.fetchReviewModeActiveContent(Number(activeContentId), activeContentType);
    },
  );

  private async fetchContentSectionRelationshipContents(contentSectionRelationshipId: number) {
    let contentSectionRelationship = await this.store.findRecord(
      'content-service/content-section-relationship',
      contentSectionRelationshipId,
    );

    this.knowledgeHubEditorService.registerActiveContent(contentSectionRelationship);
    let promises = [
      contentSectionRelationship.sourceSection.loadRelatedContent(ContentAction.EDIT),
      contentSectionRelationship.targetSection.loadRelatedContent(ContentAction.EDIT),
    ];
    await Promise.all(promises);
    return contentSectionRelationship;
  }

  private async fetchReviewModeActiveContent(
    activeContentId: number,
    activeContentType: string,
  ): Promise<(LocalizedKnowledgeContent & Model) | null> {
    let activeContent: (LocalizedKnowledgeContent & Model) | null = null;
    try {
      activeContent = await this.knowledgeHubService.findContent({
        contentId: activeContentId,
        contentTypeName: activeContentType,
      });

      if (activeContent) {
        await this.fetchContentReviewRequest(activeContent);
      }
    } catch (error) {
      return null;
    }

    if (!activeContent) {
      return null;
    }

    await activeContent.loadRelatedContent(ContentAction.EDIT);
    this.knowledgeHubEditorService.registerActiveContent(activeContent);

    return activeContent;
  }

  private async fetchContentReviewRequest(activeContent: LocalizedKnowledgeContent & Model) {
    return (activeContent as ArticleContent | ContentSnippet).contentReviewRequest;
  }

  get fromConDupeSuggestion() {
    return this.knowledgeHubDrawerEditorService.fromConDupeSuggestion;
  }

  @action
  async closeDrawer() {
    if (this.fromConDupeSuggestion) {
      await this.reloadRelatedContent();
      this.knowledgeHubDrawerEditorService.openReviewDrawer({
        activeContentId: Number(this.fromConDupeSuggestion),
        activeContentType: objectNames[EntityType.ContentSectionRelationship],
        queryParams: {
          fromConDupeSuggestion: null,
        },
      });
    }
  }

  @action
  async reloadRelatedContent() {
    let contentSectionRelationship = await this.store.findRecord(
      'content-service/content-section-relationship',
      Number(this.fromConDupeSuggestion),
    );

    if (!contentSectionRelationship) {
      return;
    }

    let promises = [
      contentSectionRelationship.sourceSection.loadRelatedContent(ContentAction.EDIT, true),
      contentSectionRelationship.targetSection.loadRelatedContent(ContentAction.EDIT, true),
    ];
    await Promise.all(promises);
  }

  @action
  toggleFilterPanel() {
    this.isFilterPanelOpen = !this.isFilterPanelOpen;
  }

  @action
  handleFilterChange() {
    // Filter change is handled by the filter panel component
    // This can be used for analytics or other side effects if needed
  }

  @action
  filterBy(reasonKey: 'contentGaps' | 'dataGaps' | 'actionGaps') {
    // Map from ReasonKey to OptimizeSuggestionItem suggestionType
    let typeMap = {
      contentGaps: 'content-gap',
      dataGaps: 'data-gap',
      actionGaps: 'action-gap',
    } as const;

    let suggestionType = typeMap[reasonKey];
    let currentFilter = this.optimizeSuggestionsService.filterState.suggestionType;

    // Toggle: if this type is already filtered, clear the filter
    let isAlreadyFiltered = currentFilter.length === 1 && currentFilter[0] === suggestionType;

    this.optimizeSuggestionsService.setFilterState({
      ...this.optimizeSuggestionsService.filterState,
      suggestionType: isAlreadyFiltered ? [] : [suggestionType],
    });
  }

  get contentGapsData() {
    // Use fullSuggestions (unfiltered) to keep bar segments at original size
    let allSuggestions = this.optimizeSuggestionsService.fullSuggestions || [];
    let contentGaps = allSuggestions.filter((s) => s?.suggestionType === 'content-gap');
    let total = allSuggestions.length || 1; // Avoid division by zero
    return {
      share: Math.round((contentGaps.length / total) * 100),
      sentToTeammate: 0,
      resolved: 0,
      unresolved: contentGaps.length,
    };
  }

  get dataGapsData() {
    let allSuggestions = this.optimizeSuggestionsService.fullSuggestions || [];
    let dataGaps = allSuggestions.filter((s) => s?.suggestionType === 'data-gap');
    let total = allSuggestions.length || 1;
    return {
      share: Math.round((dataGaps.length / total) * 100),
      sentToTeammate: 0,
      resolved: 0,
      unresolved: dataGaps.length,
    };
  }

  get actionGapsData() {
    let allSuggestions = this.optimizeSuggestionsService.fullSuggestions || [];
    let actionGaps = allSuggestions.filter((s) => s?.suggestionType === 'action-gap');
    let total = allSuggestions.length || 1;
    return {
      share: Math.round((actionGaps.length / total) * 100),
      sentToTeammate: 0,
      resolved: 0,
      unresolved: actionGaps.length,
    };
  }

  @action
  async warnIfUnsavedChanges(transition: any) {
    let previousContentId = this.knowledgeHubEditorService.activeContentModel?.entityId;
    let previousEntityType = this.knowledgeHubEditorService.activeContentModel?.entityType;
    let hasRouteChanged = transition.from?.name !== transition.to?.name;

    if (hasRouteChanged && this.suggestionService.hasDirtyContent) {
      transition.abort();
      this.isHandlingTransition = true;
      this.suggestionService.isChangingContent = true;

      try {
        let confirmationResult = await this.knowledgeHubDrawerEditorService.confirmClose();

        if (confirmationResult === true) {
          await this.knowledgeHubDrawerEditorService.performContentAction('save');
          this.isHandlingTransition = false;
          if (this.suggestionService.allReviewRequestsAreActioned) {
            await this.onAllReviewRequestsActioned(previousContentId, previousEntityType);
          }
          transition.retry();
        } else if (confirmationResult.canceled) {
          await this.knowledgeHubDrawerEditorService.performContentAction('cancel');
          this.isHandlingTransition = false;
          if (this.allReviewRequestsRejected()) {
            await this.onAllReviewRequestsActioned(previousContentId, previousEntityType);
          }
          transition.retry();
        } else {
          this.isHandlingTransition = false;
        }
      } catch (error) {
        captureException(error);
        this.isHandlingTransition = false;
      } finally {
        this.suggestionService.isChangingContent = false;
      }
    }
  }

  @action
  showRelatedConversation(conversation: ConversationExploreConversation): void {
    this.selectedConversation = conversation;
    this.showConversationPanel = true;
  }

  @action
  toggleConversationPanel(): void {
    this.showConversationPanel = !this.showConversationPanel;
  }

  get suggestedConversationIds() {
    return this.activeSuggestion.conversationIds;
  }

  private allReviewRequestsRejected() {
    return this.suggestionService.reviewRequests.every((reviewRequest) => {
      return reviewRequest.get('status') === Status.Rejected;
    });
  }

  private async onAllReviewRequestsActioned(
    previousContentId: number,
    previousEntityType: EntityType,
  ) {
    await timeout(ENV.APP._1000MS);

    this.suggestionService.contentSuggestionsService.removeSuggestionFromList({
      entity_id: previousContentId,
      entity_type: previousEntityType,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::OptimizeSuggestions::Suggestions': typeof Suggestions;
    'reporting/automation/ai-insights/optimize-suggestions/suggestions': typeof Suggestions;
  }
}
